import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Minus, Plus, Share2 } from 'lucide-react';
import type { Canvas } from 'fabric';
import { cn } from '@/lib/utils';
import type { Product, ProductPosition } from '@/api/products';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import { DirectSaleDesktopUploadPanel } from './DirectSaleDesktopUploadPanel';
import { DirectSaleDesktopMotifPanel } from './DirectSaleDesktopMotifPanel';
import {
  DirectSaleDesktopRail,
  type DirectSaleDesktopPanel,
  type DirectSaleRailAction,
} from './DirectSaleDesktopRail';
import { DirectSaleDesktopDrawer } from './DirectSaleDesktopDrawer';
import { DirectSaleObjectBar } from './DirectSaleObjectBar';
import { DirectSaleFontList } from './DirectSaleFontList';
import {
  DirectSaleTextColorGrid,
  useSelectedTextColorName,
} from './DirectSaleTextColorGrid';
import {
  DirectSaleDesktopSidebar,
  type DirectSaleSidebarHandle,
} from './DirectSaleDesktopSidebar';
import { DirectSaleDesktopCatalog } from './DirectSaleDesktopCatalog';
import {
  DirectSaleStartDialog,
  type DirectSaleStartChoice,
} from './DirectSaleStartDialog';
import { useDirectSaleShare } from './useDirectSaleShare';
import { positionsWithArtwork } from '../positions-with-artwork';

interface DesktopChromeInput {
  /** The desktop chrome is on: the window is wide and the product is loaded. */
  enabled: boolean;
  product: Product | undefined;
  canvas: Canvas | null;
  /** The positions the customer may decorate, as the design step offers them. */
  sellablePositions: ProductPosition[];
  /** The sizes the chosen colour offers. */
  sizes: { id: string; name: string }[];
  hasTextSelected: boolean;
  /** Puts a text onto the product and starts typing into it. */
  startText: () => void;
}

/** Where the design step puts the chrome: around the canvas, never in it. */
export interface DesktopChrome {
  /** Left of the canvas column. */
  rail: ReactNode;
  /** Inside the canvas column, lying over the canvas. */
  overCanvas: ReactNode;
  /** Right of the canvas column. */
  aside: ReactNode;
  /** Anywhere: they render through a portal. */
  dialogs: ReactNode;
  /** Closes whatever the chrome has open (the window left the desktop). */
  reset: () => void;
  /** A drawer lies over the canvas. */
  panelOpen: boolean;
  /** How much larger than fitted the product is shown; 1 fits the column. */
  zoom: number;
}

export const DESKTOP_ZOOM_MIN = 1;
export const DESKTOP_ZOOM_MAX = 2;
const ZOOM_STEP = 0.25;

/**
 * The desktop's chrome around the editor, after Spreadshirt's designer: a
 * tool rail, drawers lying over the canvas, the bar of the selected object,
 * the purchase column and the dialogs.
 *
 * A hook returning slots rather than a component wrapping the editor: the
 * chrome surrounds the canvas on three sides, and ProductPreview with its
 * ancestors has to keep its place in the design step's tree — a wrapper
 * that comes and goes with the window width would remount the editor and
 * lose the design. The design step places the slots as siblings.
 *
 * With `enabled` false every slot is empty and nothing listens.
 */
export function useDirectSaleDesktopChrome({
  enabled,
  product,
  canvas,
  sellablePositions,
  sizes,
  hasTextSelected,
  startText,
}: DesktopChromeInput): DesktopChrome {
  const { t } = useTranslation();
  const isCurrentStep = useDirectSaleStore((s) => s.currentStep === 'design');
  const hasDecoration = useDirectSaleStore(
    (s) => s.decoratedPositions.length > 0
  );
  const masterPositions = useCanvasStateManager((s) => s.masterPositions);
  const activePosition = useProductSelectionStore((s) => s.activePosition);

  // Ephemeral by design: a resumed session must not come back with a
  // drawer or a dialog already open.
  const [panel, setPanel] = useState<DirectSaleDesktopPanel | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  // The editor fits the product into its box: a box grown beyond the column
  // is what zooms it, and the column scrolls the rest. Nothing in the editor
  // core changes for that.
  const [zoom, setZoom] = useState(DESKTOP_ZOOM_MIN);
  const clampZoom = (value: number) =>
    Math.min(
      DESKTOP_ZOOM_MAX,
      Math.max(DESKTOP_ZOOM_MIN, Math.round(value * 100) / 100)
    );
  const selectedTextColorName = useSelectedTextColorName();
  // Once per product: the page keys the design step by product, so a mount
  // is one.
  const startShownRef = useRef(false);
  const sidebarRef = useRef<DirectSaleSidebarHandle>(null);
  /** What had the focus when the drawer opened; gets it back on close. */
  const drawerOpenerRef = useRef<HTMLElement | null>(null);

  const reset = () => {
    setPanel(null);
    setCatalogOpen(false);
    setStartOpen(false);
    setZoom(DESKTOP_ZOOM_MIN);
  };

  // The font and colour drawers belong to a selected text.
  useEffect(() => {
    if (hasTextSelected) return;
    setPanel((prev) => (prev === 'font' || prev === 'color' ? null : prev));
  }, [hasTextSelected]);

  // A placement drawer left open across a position switch would drop its
  // upload or motif onto the position the customer just left.
  useEffect(() => {
    setPanel((prev) => (prev === 'upload' || prev === 'motifs' ? null : prev));
  }, [activePosition]);

  /** Opens a drawer, or closes it when it is the one already open. */
  const togglePanel = (next: DirectSaleDesktopPanel) => {
    if (panel === null && document.activeElement instanceof HTMLElement) {
      drawerOpenerRef.current = document.activeElement;
    }
    setPanel((prev) => (prev === next ? null : next));
  };

  /**
   * The rail's tools and the start dialog's tiles lead the same way. "Text"
   * and "Produkte" have no drawer and close an open one. The start dialog
   * reports its tile only once it has closed and let go of the focus.
   */
  const runAction = (
    action: DirectSaleRailAction | DirectSaleStartChoice,
    from: 'rail' | 'start' = 'rail'
  ) => {
    setStartOpen(false);
    if (action === 'upload' || action === 'motifs') {
      // The rail's tool toggles its drawer; a tile asks for it and must
      // not close one that is already open.
      if (from === 'start') setPanel(action);
      else togglePanel(action);
      return;
    }
    setPanel(null);
    if (action === 'products') setCatalogOpen(true);
    else startText();
  };

  /**
   * Before the cart button decides anything: end a text input still going
   * on and store the canvas as it is now. The editor only stores a text
   * when its typing ends — without this the purchase would carry the
   * placeholder or the words from before. Returns whether the product
   * carries a design after that; a placeholder left untouched is removed by
   * the ending and leaves none.
   */
  const prepareCart = (): boolean => {
    const active = canvas?.getActiveObject() as
      | { isEditing?: boolean; exitEditing?: () => void }
      | undefined;
    if (active?.isEditing) {
      active.exitEditing?.();
      canvas?.requestRenderAll();
    }
    try {
      useCanvasStateManager.getState().saveCurrentPosition();
    } catch {
      // Canvas not ready for serialization; the stored state counts.
    }
    const stored = useCanvasStateManager.getState().masterPositions;
    return positionsWithArtwork(sellablePositions, stored).length > 0;
  };

  // A product without a design asks once what should go on it. Tied to the
  // step being the current one: the step is already mounted (hidden) while
  // the page still asks whether to resume, and dialogs render through a
  // portal that a hidden container does not hide.
  const hasStoredArtwork = Object.values(masterPositions).some(
    (position) => (position?.json?.objects?.length ?? 0) > 0
  );
  useEffect(() => {
    if (!enabled || !isCurrentStep) return;
    if (startShownRef.current) return;
    startShownRef.current = true;
    if (!hasDecoration && !hasStoredArtwork) setStartOpen(true);
  }, [enabled, isCurrentStep, hasDecoration, hasStoredArtwork]);

  // Escape lets go of one layer per press: a dialog first (Radix), typing
  // on the product next (Fabric), then the drawer or the size list, then
  // the selection. Only the last two are ours.
  useEffect(() => {
    if (!enabled || !isCurrentStep) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) {
        return;
      }
      // Fabric types into a hidden textarea and ends the typing itself.
      if (event.target instanceof HTMLTextAreaElement) return;
      if (sidebarRef.current?.closeTransient()) return;
      if (panel !== null) {
        setPanel(null);
        return;
      }
      if (canvas?.getActiveObject()) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, isCurrentStep, panel, canvas]);

  if (!enabled || !product) {
    return {
      rail: null,
      overCanvas: null,
      aside: null,
      dialogs: null,
      reset,
      panelOpen: false,
      zoom: DESKTOP_ZOOM_MIN,
    };
  }

  const drawerTitle =
    panel === 'upload'
      ? t('directSale.desktop.uploads')
      : panel === 'motifs'
        ? t('directSale.design.motifs')
        : panel === 'font'
          ? t('directSale.desktop.fonts')
          : t('directSale.design.textColor');

  const zoomButton =
    'flex h-9 w-9 items-center justify-center rounded-full text-ink transition-colors hover:bg-paper-2 disabled:text-ink-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

  return {
    reset,
    panelOpen: panel !== null,
    zoom,
    // The rail as a card of its own, like the other two columns.
    rail: (
      <div className="flex-none overflow-hidden rounded-section border border-line bg-paper">
        <DirectSaleDesktopRail activePanel={panel} onAction={runAction} />
      </div>
    ),
    overCanvas: (
      <>
        {panel !== null && (
          <DirectSaleDesktopDrawer
            // A drawer per tool: switching tools moves the focus anew.
            key={panel}
            title={drawerTitle}
            subtitle={
              panel === 'color'
                ? (selectedTextColorName ?? undefined)
                : undefined
            }
            onClose={() => setPanel(null)}
            returnFocusRef={drawerOpenerRef}
          >
            {/* Completion closes are scoped to the initiating drawer: a
                background upload can finish after its drawer is gone, and
                an unconditional close would shut whichever one the customer
                switched to meanwhile. */}
            {panel === 'upload' && (
              <DirectSaleDesktopUploadPanel
                onClose={() =>
                  setPanel((prev) => (prev === 'upload' ? null : prev))
                }
              />
            )}
            {panel === 'motifs' && (
              <DirectSaleDesktopMotifPanel
                onClose={() =>
                  setPanel((prev) => (prev === 'motifs' ? null : prev))
                }
              />
            )}
            {panel === 'font' && (
              <div className="p-4">
                <DirectSaleFontList />
              </div>
            )}
            {panel === 'color' && (
              <div className="p-4">
                <DirectSaleTextColorGrid />
              </div>
            )}
          </DirectSaleDesktopDrawer>
        )}
        {/* Fixed zones over the canvas: the object bar at the top (a drawer
            lies over it rather than pushing it — nothing on the canvas moves
            when a drawer opens), sharing in the top right corner, the zoom in
            the bottom left. */}
        <div className="pointer-events-none absolute left-4 right-[68px] top-3 z-10 flex justify-center [&>*]:pointer-events-auto">
          <DirectSaleObjectBar
            activePanel={panel === 'font' || panel === 'color' ? panel : null}
            onOpenPanel={togglePanel}
          />
        </div>
        <DesktopShareButton product={product} />
        <div
          role="group"
          aria-label={t('directSale.desktop.zoom')}
          className="absolute bottom-3 left-3 z-10 flex flex-col items-center gap-1 rounded-full border border-line bg-paper p-1"
        >
          <button
            type="button"
            aria-label={t('directSale.desktop.zoomIn')}
            disabled={zoom >= DESKTOP_ZOOM_MAX}
            onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
            className={zoomButton}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </button>
          <input
            type="range"
            aria-label={t('directSale.desktop.zoom')}
            min={DESKTOP_ZOOM_MIN}
            max={DESKTOP_ZOOM_MAX}
            step={0.05}
            value={zoom}
            onChange={(event) => setZoom(clampZoom(Number(event.target.value)))}
            // Vertical: the slider runs bottom-up like Spreadshirt's.
            className="h-28 w-9 cursor-pointer accent-primary-500 [direction:rtl] [writing-mode:vertical-lr]"
          />
          <button
            type="button"
            aria-label={t('directSale.desktop.zoomOut')}
            disabled={zoom <= DESKTOP_ZOOM_MIN}
            onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
            className={zoomButton}
          >
            <Minus className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </>
    ),
    aside: (
      <aside className="w-[380px] flex-none overflow-hidden rounded-section border border-line bg-paper min-[1200px]:w-[470px]">
        <DirectSaleDesktopSidebar
          ref={sidebarRef}
          product={product}
          sizes={sizes}
          onRequestStart={() => setStartOpen(true)}
          prepareCart={prepareCart}
        />
      </aside>
    ),
    dialogs: isCurrentStep ? (
      <>
        <DirectSaleStartDialog
          open={startOpen}
          onClose={() => setStartOpen(false)}
          onPick={(choice) => runAction(choice, 'start')}
        />
        <DirectSaleDesktopCatalog
          open={catalogOpen}
          onOpenChange={setCatalogOpen}
          product={product}
        />
      </>
    ) : null,
  };
}

/**
 * Sharing, in the canvas' top right corner: the product with the design as
 * a picture. A component of its own because the picture is composed from
 * the loaded product.
 */
function DesktopShareButton({ product }: { product: Product }) {
  const { t } = useTranslation();
  const { canShare, share, outcome } = useDirectSaleShare(product);

  return (
    <div className="absolute right-3 top-3 z-10 flex flex-col items-end gap-2">
      <button
        type="button"
        aria-label={t('directSale.mobile.share')}
        title={t('directSale.mobile.share')}
        disabled={!canShare}
        onClick={share}
        className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-paper text-ink transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:text-ink-3 disabled:hover:border-line"
      >
        <Share2 className="h-5 w-5" />
      </button>
      {outcome && (
        <p
          role="status"
          className={cn(
            'flex items-center gap-1.5 whitespace-nowrap rounded-control border border-line bg-paper px-3 py-1.5 text-xs font-semibold',
            outcome === 'saved' ? 'text-ink' : 'text-danger'
          )}
        >
          {outcome === 'saved' && <Check className="h-3.5 w-3.5" />}
          {outcome === 'saved'
            ? t('directSale.mobile.pictureSaved')
            : t('directSale.mobile.shareFailed')}
        </p>
      )}
    </div>
  );
}
